import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import { get, map, filter, sortBy } from "lodash";

import { fetchPut } from "../../../api/fetch";

import Grid from "@material-ui/core/Grid";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";

import TableCellStyled from "../../Common/TableCellStyled";
import PictureROI from "../../Common/PictureROI";
import EllipsedTextNew from "../../Common/EllipsedTextNew";

import { getUser } from "../../../utils/users";
import { getLovValue, getAttributeValue } from "../../../utils/profile";
import { formatDate } from "../../../utils/date";
import {
  getMeasureType,
  isOverThreshold,
  getThresholdEvent,
  displayedTemperature,
  getMaxThermalPicture,
} from "../../../utils/temperature";

import { ISO_TYPE, MANUAL_TYPE } from "../../../constants/common";

import colors from "../../../assets/style/colors";
import images from "../../../assets/img/images";

const styles = {
  colorCircle: {
    width: "14px",
    height: "14px",
    borderRadius: "50%",
  },
  tableCell: {
    color: colors.black54,
  },
  tableCellCenter: {
    textAlign: "center !important",
  },
};

const excludeAttr = [153, 164, 165, 166, 167, 168, 88, 187];

const TemperatureMetricsTableRow = (props) => {
  const {
    classes,
    item,
    eventGroup,
    index,
    language,
    lov,
    startTime,
    showAllColumns,
    setFocusPhotoEvent,
    refresh,
    archived = false,
  } = props;

  const [userName, setUserName] = React.useState();
  const [isRemoving, setIsRemoving] = React.useState(false);

  const type = getMeasureType(item);
  const maxThermalPicture = getMaxThermalPicture(item);

  React.useEffect(() => {
    const fetchUserName = async () => {
      try {
        let user = await getUser(get(item, ["created_by"]));
        setUserName(
          `${get(user, ["profile", "firstname"])} ${get(user, [
            "profile",
            "lastname",
          ])}`
        );
      } catch (e) {
        console.log(e);
      }
    };
    if (!isNaN(parseInt(get(item, ["created_by"])))) {
      fetchUserName();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const archiveItem = async () => {
    setIsRemoving(true);
    try {
      await fetchPut(`event_groups/${eventGroup}/events/${get(item, ["id"])}`, {
        active: false,
      });
      refresh();
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <TableRow
      style={{
        backgroundColor: index % 2 === 0 ? colors.white : colors.tableDarkBg,
      }}
    >
      <TableCellStyled className={classes.tableCellCenter}>
        {!archived && !isRemoving && (
          <img
            src={images.deleteIcon}
            alt="delete"
            style={{ cursor: "pointer" }}
            onClick={archiveItem}
          />
        )}
        {isRemoving && <CircularProgress size={14} />}
      </TableCellStyled>
      <TableCellStyled className={classes.tableCell}>
        {startTime
          ? formatDate(
              new Date(
                type === MANUAL_TYPE
                  ? item.created_on
                  : getAttributeValue(item, 13)
              ) - new Date(startTime),
              "mm:ss"
            )
          : " "}
      </TableCellStyled>
      <TableCellStyled className={classes.tableCell}>
        <div>{userName || I18n.get("common_luke")}</div>
        <div>
          {formatDate(
            type === MANUAL_TYPE ? item.created_on : getAttributeValue(item, 13)
          )}
        </div>
      </TableCellStyled>
      <TableCellStyled>
        <PictureROI
          key={get(maxThermalPicture, ["id"])}
          url={get(maxThermalPicture, ["url"])}
          roiOverlay={get(maxThermalPicture, ["roi_overlay"])}
          thermalPoint={get(maxThermalPicture, ["thermal_pixel_coordinate"])}
          initialSquareSize={{ width: 65, height: 48 }}
          id={get(maxThermalPicture, ["id"])}
          onClick={props.blockLevel2 ? null : () => setFocusPhotoEvent(item)}
          showRoi={true}
          noZoom
        />
      </TableCellStyled>
      <TableCellStyled>
        <Grid container alignItems="center">
          <Grid
            item
            className={classes.colorCircle}
            style={{
              backgroundColor:
                type === ISO_TYPE
                  ? colors.isoYellow
                  : type === MANUAL_TYPE
                  ? colors.manualTempBlue
                  : colors.bodyPink,
            }}
          />
          <Grid item className="text-54" style={{ marginLeft: "10px" }}>
            {I18n.get(type === ISO_TYPE ? "site_iso" : "site_body")}
          </Grid>
        </Grid>
      </TableCellStyled>
      <TableCellStyled className={classes.tableCell}>
        {type === MANUAL_TYPE && displayedTemperature(item, 187, lov)}
      </TableCellStyled>
      <TableCellStyled>
        {type !== MANUAL_TYPE && (
          <span
            className="flexCenter"
            style={{
              justifyContent: "center",
              width: "55px",
              height: "24px",
              borderRadius: "2px",
              color: isOverThreshold(
                getAttributeValue(item, type === ISO_TYPE ? 168 : 165),
                getThresholdEvent(item)
              )
                ? colors.threatRed
                : colors.cardGreen,
              backgroundColor: isOverThreshold(
                getAttributeValue(item, type === ISO_TYPE ? 168 : 165),
                getThresholdEvent(item)
              )
                ? colors.threatRed10
                : colors.cardGreen10,
            }}
          >
            {displayedTemperature(item, type === ISO_TYPE ? 168 : 165, lov)}
          </span>
        )}
      </TableCellStyled>
      <TableCellStyled className={classes.tableCell}>
        {type !== MANUAL_TYPE &&
          displayedTemperature(item, type === ISO_TYPE ? 167 : 164, lov)}
      </TableCellStyled>
      <TableCellStyled className={classes.tableCell}>
        {type !== MANUAL_TYPE &&
          displayedTemperature(item, type === ISO_TYPE ? 166 : 153, lov)}
      </TableCellStyled>
      {showAllColumns && (
        <TableCellStyled className={classes.tableCell}>
          {get(item, ["id"])}
        </TableCellStyled>
      )}
      {showAllColumns &&
        map(
          sortBy(
            filter(
              get(item, ["attributes"]),
              (attr) => !excludeAttr.includes(attr.id)
            ),
            (attr) => get(attr, ["app_display_seq_numb"])
          ),
          (attr) => (
            <TableCellStyled key={attr.id} className={classes.tableCell}>
              <EllipsedTextNew
                text={
                  get(attr, ["data_type"]) === "lov(picker)"
                    ? getLovValue(88, get(attr, ["value"]), lov, language)
                    : get(attr, ["data_type"]) === "float" &&
                      get(attr, ["value"])
                    ? parseFloat(get(attr, ["value"])).toFixed(1)
                    : get(attr, ["data_type"]) === "datetime"
                    ? formatDate(get(attr, ["value"]))
                    : get(attr, ["value"]) || "-"
                }
                maxWidth={600}
                maxHeight={150}
              />
            </TableCellStyled>
          )
        )}
    </TableRow>
  );
};

export default withStyles(styles)(TemperatureMetricsTableRow);
